import React from 'react';
import { Tooltip } from 'antd-v5';
import { TFunction } from 'i18next';
import { ExclamationCircleIcon } from 'assets/icons';
import {
  ASC,
  ASC_EXADIS,
  ClaimFormType,
  DIRECT_DELIVERY,
  FULL_OTC,
  MULTI_ITEMS_DEPOSIT_RETURN,
  MULTI_ITEMS_EXADIS_RETURN,
} from 'domains/order/Order.types';

import ClaimStatus from 'pages/MyOrdersPage/OrderClaims/ClaimStatus';
import { theme } from 'styles';
import { Flex, Icon } from 'UI';

interface DropdownItem {
  value: string;
  title: string;
}

export const getReasons = (t: TFunction): DropdownItem[] => [
  { value: 'Y10', title: t('order.claim.reason.wrong_price', 'Wrong Price') },
  { value: 'Y01', title: t('order.claim.reason.additional_part_received', 'Additional Part Received') },
  {
    value: 'Y01N',
    title: t(
      'order.claim.reason.additional_part_received_without_order_invoice',
      'Additional Part Received without order/invoice',
    ),
  },
  { value: 'Y02', title: t('order.claim.reason.missing_part', 'Missing Part') },
  { value: 'Y03', title: t('order.claim.reason.defective_part', 'Defective Part') },
  {
    value: 'Y3T',
    title: t('order.claim.reason.defective_part_with_blocking_note', 'Defective part with blocking note'),
  },
  { value: 'Y04', title: t('order.claim.reason.damaged_part', 'Damaged Part') },
  { value: 'Y05', title: t('order.claim.reason.wrong_part', 'Wrong Part') },
  { value: 'Y06', title: t('order.claim.reason.recipient_error', 'Recipient error') },
  { value: 'Y07', title: t('order.claim.reason.missing_package', 'Missing Package') },
  { value: 'Y08', title: t('order.claim.reason.missing_line', 'Missing line') },
  { value: 'Y09', title: t('order.claim.reason.unused_part', 'Unused Part') },
  { value: 'Y9A', title: t('order.claim.reason.unused_part_exadis', 'Unused Part (Exadis)') },
  { value: 'Y9G', title: t('order.claim.reason.warranty', 'Warranty') },
];

export const getReasonsForFormType = (t: TFunction, formType: ClaimFormType): DropdownItem[] => {
  switch (formType) {
    case DIRECT_DELIVERY:
      return getReasons(t).filter((r) => r.value !== 'Y3T' && r.value !== 'Y9G');
    case ASC:
    case FULL_OTC:
      return getReasons(t).filter((r) => r.value !== 'Y9G');
    case ASC_EXADIS:
      return getReasons(t);
    case MULTI_ITEMS_EXADIS_RETURN:
      return getReasons(t).filter((r) => r.value === 'Y09');
    case MULTI_ITEMS_DEPOSIT_RETURN:
      return getReasons(t).filter((r) => r.value === 'Y9A');
    default:
      return getReasons(t);
  }
};

export const getReasonLabel = (t: TFunction, code: string): string => {
  const reasons = getReasons(t);
  return reasons.find((r) => r.value === code)?.title ?? '';
};

export const getTypeLabel = (t: TFunction, code?: string): string => {
  switch (code) {
    case 'ZCMR':
    case 'ZGA2':
      return t('order.claim.type.credit_memo_request', 'Credit Memo Request');
    case 'ZDMR':
      return t('order.claim.type.debit_memo_request', 'Debit Memo Request');
    case 'ZACR':
      return t('order.claim.type.advance_return_order', 'Advance Return Order');
    case 'ZRVD':
      return t('order.claim.type.return_notif_vendor', 'Return notif vendor');
    default:
      return '';
  }
};

export const getAttachmentTitleLabel = (t: TFunction, reason?: string, option?: string) => {
  const labelAddFile = t('order.claim.file.add_file_here', 'Add your file here');
  const labelAddSheetAndPart = t('order.claim.file.add_sheet_and_part', 'Add Delivery sheet AND Part Picture');
  const labelDeliverySheet = t('order.claim.file.add_sheet', 'Delivery sheet');
  switch (reason) {
    case 'Y01':
    case 'Y01N':
      return option === '1' ? labelAddFile : labelAddSheetAndPart;
    case 'Y02':
    case 'Y08':
    case 'Y10':
      return labelAddFile;
    case 'Y03':
    case 'Y04':
    case 'Y06':
    case 'Y09':
    case 'Y9A':
      return labelAddSheetAndPart;
    case 'Y05':
    case 'Y07':
      return labelDeliverySheet;
    default:
      return labelAddFile;
  }
};

export const getClaimStatus = (t: TFunction, status: string, rejectionLabel?: string): React.ReactNode => {
  switch (status.toUpperCase()) {
    case 'IN_PROGRESS':
    case 'OK':
      return <ClaimStatus status={t('order.claim.status.in_progress', 'In progress')} statusType={'warning'} />;
    case 'ACCEPTED':
      return <ClaimStatus status={t('order.claim.status.accepted', 'Accepted')} statusType={'success'} />;
    case 'REFUSED':
      return (
        <Tooltip title={rejectionLabel}>
          <Flex gap={10} align={'center'}>
            <Icon IconComponent={ExclamationCircleIcon} size={24} color={theme.color.error} />
            <ClaimStatus status={t('order.claim.status.rejected', 'Rejected')} statusType={'error'} />
          </Flex>
        </Tooltip>
      );
    default:
      return <>{status}</>;
  }
};

export const getReasonOption = (t: TFunction): Map<string, DropdownItem[]> => {
  const defaultLabels = [
    { value: '0', title: t('order.claim.option.do_not_accept', 'Do not accept') },
    { value: '1', title: t('order.claim.option.accept', 'Accept') },
  ];
  return new Map([
    [
      'Y10',
      [
        { value: '0', title: t('order.claim.option.less_paid', 'Less paid') },
        { value: '1', title: t('order.claim.option.too_much_paid', 'Too much paid') },
      ],
    ],
    ['Y01', defaultLabels],
    ['Y01N', defaultLabels],
    ['Y05', defaultLabels],
    ['Y06', defaultLabels],
  ]);
};

export const getAmountLabel = (t: TFunction, reason: string) => {
  if (reason === 'Y10') {
    return t('common.gap_amount', 'Gap amount');
  }
  return t('common.amount', 'Amount');
};
